// Costruzione del payload di una misura Web Vital per `POST …/web_vitals`
// (CYJS-12, esteso a TTFB e FCP in CYJS-28). Campi snake_case come il resto del
// contratto wire, gemello di `pageviews.ts`: nessun segnale identificante nel
// body — la misura è (metrica, valore, pagina), IP e User-Agent li ricava il
// server dalla richiesta.
// `path` è sempre privo di query string e frammento, come per le pageview.
//
// L'`esito` (good/needs-improvement/poor) NON viaggia: lo calcola il backend
// dalle soglie ufficiali. Mandarlo significherebbe duplicare qui quelle soglie e
// lasciare che sia il misurato a darsi il voto — un SDK vecchio dipingerebbe di
// verde quello che oggi è rosso.
//
// Assume un contesto browser (l'unico chiamante, l'integrazione web vitals, gira
// solo lì): niente guardie non-browser, sarebbe codice morto.

import { uuidV4 } from './uuid'

/// Le metriche raccolte. I tre Core Web Vitals con cui Google giudica un sito —
/// caricamento percepito (LCP), stabilità del layout (CLS), reattività alle
/// interazioni (INP) — più le due diagnostiche che spiegano un LCP alto: il
/// tempo di risposta del server (TTFB) e il primo contenuto dipinto (FCP).
export type WebVitalName = 'lcp' | 'cls' | 'inp' | 'ttfb' | 'fcp'

/// Tipi di navigazione riconosciuti dal backend. La Navigation Timing API usa
/// `back_forward` con l'underscore: la traduzione vive in
/// `normalizeNavigationType`, un tipo non tradotto verrebbe scartato lato server.
const NAVIGATION_TYPES = [
  'navigate',
  'reload',
  'back-forward',
  'prerender',
  'restore',
] as const

/// Payload di una misura, SENZA `environment`/`release` (li aggiunge il client:
/// è lui a conoscere la configurazione risolta). Type alias e non interfaccia:
/// così resta assegnabile a `EventPayload` quando il client lo spedisce.
export type WebVitalPayload = {
  event_id: string
  metric: WebVitalName
  value: number
  hostname: string
  path: string
  navigation_type?: string
  occurred_at: string
}

export interface WebVitalInput {
  metric: WebVitalName
  value: number
  /// Path della pagina misurata (default `location.pathname`). Query e frammento
  /// sono comunque strippati.
  path?: string | undefined
  /// Tipo di navigazione grezzo (`PerformanceNavigationTiming.type`).
  navigationType?: string | undefined
}

/// Costruisce la misura dalla location corrente. `event_id` è un UUID v4 (con
/// trattini) per l'idempotenza `(project_id, event_id)`, come per le pageview.
export function buildWebVital(input: WebVitalInput): WebVitalPayload {
  const navigationType = normalizeNavigationType(input.navigationType)

  return {
    event_id: uuidV4(),
    metric: input.metric,
    value: roundWebVital(input.metric, input.value),
    hostname: location.hostname,
    path: stripPath(input.path ?? location.pathname),
    ...(navigationType ? { navigation_type: navigationType } : {}),
    occurred_at: new Date().toISOString(),
  }
}

/// Tipo di navigazione nel vocabolario del backend, o `undefined` se ignoto (un
/// valore inventato verrebbe scartato: meglio non mandarlo affatto). Ci finiscono
/// anche i tipi che web-vitals conosce e l'ingest no (`back-forward-cache`,
/// `soft-navigation`): la misura parte comunque, solo senza il tipo — perderla
/// per un campo accessorio sarebbe uno scambio pessimo.
export function normalizeNavigationType(raw: string | undefined): string | undefined {
  if (!raw) return undefined
  const normalized = raw.trim().toLowerCase().replace('back_forward', 'back-forward')
  return (NAVIGATION_TYPES as readonly string[]).includes(normalized)
    ? normalized
    : undefined
}

/// Arrotondamento per metrica: il CLS è adimensionale e si muove su valori
/// piccoli (4 decimali), le altre sono millisecondi e la frazione non aggiunge
/// nulla a un percentile.
export function roundWebVital(metric: WebVitalName, value: number): number {
  if (metric === 'cls') return Math.round(value * 10000) / 10000
  return Math.round(value)
}

// Solo il pathname: la query può portare token e identificativi, e una misura di
// velocità non ne ha alcun bisogno.
function stripPath(path: string): string {
  return path.split(/[?#]/, 1)[0] ?? path
}
